import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCardModule } from '@angular/material/card';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { IosNotificationService } from '../../services/ios-notification.service';
import { PwaInstallGuideComponent } from '../pwa-install-guide/pwa-install-guide.component';
import { PwaInstallIconsComponent } from '../pwa-install-icons/pwa-install-icons.component';

@Component({
  selector: 'app-pwa-install-prompt',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatStepperModule,
    MatCardModule,
    PwaInstallGuideComponent,
    PwaInstallIconsComponent
  ],
  template: `
    <!-- Prompt Complet -->
    <div class="pwa-prompt-overlay" *ngIf="showFullPrompt" @fadeIn>
      <div class="pwa-prompt-card">
        <!-- Header avec logo -->
        <div class="prompt-header">
          <img src="/assets/icons/icon-192x192.png" alt="Logo" class="app-logo">
          <button mat-icon-button class="close-btn" (click)="minimizePrompt()">
            <mat-icon>minimize</mat-icon>
          </button>
        </div>

        <!-- Titre et description -->
        <div class="prompt-content">
          <h2>📱 Installer Vendeur.site</h2>
          <p class="subtitle">Profitez d'une expérience native avec accès rapide et notifications</p>

          <!-- Avantages -->
          <div class="benefits">
            <div class="benefit-item">
              <mat-icon>offline_bolt</mat-icon>
              <span>Fonctionne hors ligne</span>
            </div>
            <div class="benefit-item">
              <mat-icon>notifications_active</mat-icon>
              <span>Notifications en temps réel</span>
            </div>
            <div class="benefit-item">
              <mat-icon>speed</mat-icon>
              <span>Plus rapide et fluide</span>
            </div>
            <div class="benefit-item">
              <mat-icon>home</mat-icon>
              <span>Icône sur l'écran d'accueil</span>
            </div>
          </div>

          <!-- Guide d'installation selon la plateforme -->
          <div class="installation-guide" [ngSwitch]="platform">
            <!-- Guide iOS -->
            <div *ngSwitchCase="'ios'" class="platform-guide">
              <h3>📲 Installation sur iPhone/iPad</h3>
              <div class="steps">
                <div class="step" [class.completed]="currentStep > 1">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <strong>Ouvrez dans Safari</strong>
                    <p>⚠️ Utilisez Safari, pas Chrome</p>
                  </div>
                </div>
                <div class="step" [class.completed]="currentStep > 2">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <strong>Appuyez sur <mat-icon inline>ios_share</mat-icon></strong>
                    <p>Bouton partage en bas</p>
                  </div>
                </div>
                <div class="step" [class.completed]="currentStep > 3">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <strong>"Sur l'écran d'accueil"</strong>
                    <p>Faites défiler et sélectionnez</p>
                  </div>
                </div>
                <div class="step" [class.completed]="currentStep > 4">
                  <div class="step-number">4</div>
                  <div class="step-content">
                    <strong>Appuyez sur "Ajouter"</strong>
                    <p>En haut à droite</p>
                  </div>
                </div>
              </div>
              <div class="ios-warning" *ngIf="!isInSafari">
                <mat-icon>warning</mat-icon>
                <span>Vous devez utiliser Safari pour installer sur iOS</span>
              </div>
            </div>

            <!-- Guide Android -->
            <div *ngSwitchCase="'android'" class="platform-guide">
              <h3>📲 Installation sur Android</h3>
              <div class="steps">
                <div class="step" [class.completed]="currentStep > 1">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <strong>Appuyez sur <mat-icon inline>more_vert</mat-icon></strong>
                    <p>Menu en haut à droite</p>
                  </div>
                </div>
                <div class="step" [class.completed]="currentStep > 2">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <strong>"Installer l'application"</strong>
                    <p>ou "Ajouter à l'écran d'accueil"</p>
                  </div>
                </div>
                <div class="step" [class.completed]="currentStep > 3">
                  <div class="step-number">3</div>
                  <div class="step-content">
                    <strong>Confirmer l'installation</strong>
                    <p>Appuyez sur "Installer"</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Guide Desktop -->
            <div *ngSwitchCase="'desktop'" class="platform-guide">
              <h3>💻 Installation sur Ordinateur</h3>
              <div class="steps">
                <div class="step" [class.completed]="currentStep > 1">
                  <div class="step-number">1</div>
                  <div class="step-content">
                    <strong>Icône d'installation <mat-icon inline>install_desktop</mat-icon></strong>
                    <p>Dans la barre d'adresse</p>
                  </div>
                </div>
                <div class="step" [class.completed]="currentStep > 2">
                  <div class="step-number">2</div>
                  <div class="step-content">
                    <strong>Cliquez sur "Installer"</strong>
                    <p>Dans la fenêtre qui apparaît</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Plateforme non supportée -->
            <div *ngSwitchDefault class="platform-guide">
              <p>Installation automatique non disponible sur cette plateforme.</p>
              <p>Ajoutez manuellement le site à vos favoris pour un accès rapide.</p>
            </div>
          </div>

          <!-- Animation d'aide -->
          <div class="help-animation" *ngIf="showAnimation">
            <!-- Version avec icônes animées (plus léger) -->
            <app-pwa-install-icons 
              [platform]="platform" 
              [step]="currentStep">
            </app-pwa-install-icons>
            
            <!-- Version SVG détaillée (optionnelle, commentée par défaut) -->
            <!-- <app-pwa-install-guide 
              [platform]="platform" 
              [animationStep]="currentStep">
            </app-pwa-install-guide> -->
          </div>

          <!-- Actions -->
          <div class="prompt-actions">
            <button mat-button (click)="installLater()" class="later-btn">
              <mat-icon>schedule</mat-icon>
              Plus tard
            </button>
            <button mat-raised-button color="primary" (click)="startInstallation()" *ngIf="canInstall">
              <mat-icon>get_app</mat-icon>
              Installer maintenant
            </button>
            <button mat-raised-button color="accent" (click)="confirmInstallation()" *ngIf="!canInstall">
              <mat-icon>check_circle</mat-icon>
              J'ai installé l'app
            </button>
          </div>
          
          <!-- Option ne plus afficher -->
          <div class="never-show-option">
            <button mat-button (click)="neverShowAgain()" class="never-btn">
              <mat-icon>do_not_disturb</mat-icon>
              Ne plus afficher
            </button>
          </div>

          <!-- Aide supplémentaire -->
          <div class="help-text">
            <button mat-button (click)="toggleAnimation()" class="help-btn">
              <mat-icon>help_outline</mat-icon>
              {{ showAnimation ? 'Masquer' : 'Voir' }} l'animation d'aide
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Prompt Réduit (Bouton flottant) -->
    <button 
      class="floating-install-btn"
      *ngIf="showMinimizedPrompt && !showFullPrompt"
      @slideIn
      (click)="reopenPrompt()"
      mat-fab
      color="primary">
      <mat-icon>get_app</mat-icon>
    </button>

    <!-- Tooltip au survol du bouton réduit -->
    <div class="floating-tooltip" *ngIf="showTooltip && showMinimizedPrompt && !showFullPrompt">
      Installer l'application
    </div>
  `,
  styles: [`
    /* Overlay et carte principale */
    .pwa-prompt-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      backdrop-filter: blur(5px);
    }

    .pwa-prompt-card {
      background: white;
      border-radius: 20px;
      max-width: 500px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
      animation: slideUp 0.3s ease-out;
    }

    /* Header */
    .prompt-header {
      background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
      padding: 20px;
      border-radius: 20px 20px 0 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .app-logo {
      width: 60px;
      height: 60px;
      border-radius: 12px;
      background: white;
      padding: 5px;
    }

    .close-btn {
      color: white;
    }

    /* Contenu */
    .prompt-content {
      padding: 24px;
    }

    .prompt-content h2 {
      margin: 0 0 8px 0;
      font-size: 24px;
      color: #333;
    }

    .subtitle {
      color: #666;
      margin: 0 0 24px 0;
    }

    /* Avantages */
    .benefits {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
      margin-bottom: 24px;
      padding: 16px;
      background: #f5f5f5;
      border-radius: 12px;
    }

    .benefit-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #555;
    }

    .benefit-item mat-icon {
      color: #1976d2;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    /* Guide d'installation */
    .platform-guide h3 {
      font-size: 18px;
      margin: 0 0 16px 0;
      color: #333;
    }

    .steps {
      margin-bottom: 20px;
    }

    .step {
      display: flex;
      align-items: flex-start;
      margin-bottom: 16px;
      padding: 12px;
      background: #f9f9f9;
      border-radius: 8px;
      transition: all 0.3s ease;
    }

    .step.completed {
      background: #e8f5e9;
    }

    .step-number {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: #1976d2;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .step.completed .step-number {
      background: #4caf50;
    }

    .step-content {
      flex: 1;
    }

    .step-content strong {
      display: block;
      margin-bottom: 4px;
      color: #333;
    }

    .step-content p {
      margin: 0;
      font-size: 13px;
      color: #666;
    }

    .step-content mat-icon {
      vertical-align: middle;
      font-size: 18px;
      width: 18px;
      height: 18px;
    }

    /* Warning iOS */
    .ios-warning {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px;
      background: #fff3e0;
      border-radius: 8px;
      color: #e65100;
      margin-top: 16px;
    }

    .ios-warning mat-icon {
      color: #ff9800;
    }

    /* Animation d'aide */
    .help-animation {
      margin: 20px 0;
      display: flex;
      justify-content: center;
      animation: fadeIn 0.5s ease-in;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    /* Actions */
    .prompt-actions {
      display: flex;
      gap: 12px;
      margin-top: 24px;
    }

    .later-btn {
      flex: 1;
    }

    .prompt-actions button {
      flex: 1;
    }

    .help-text {
      text-align: center;
      margin-top: 16px;
    }

    .help-btn {
      color: #666;
    }

    .never-show-option {
      text-align: center;
      margin-top: 8px;
      padding-top: 12px;
      border-top: 1px solid #eee;
    }

    .never-btn {
      color: #999;
      font-size: 12px;
    }

    .never-btn mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    /* Bouton flottant réduit */
    .floating-install-btn {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 9998;
      animation: pulse 2s infinite;
    }

    .floating-tooltip {
      position: fixed;
      bottom: 90px;
      right: 24px;
      background: #333;
      color: white;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 14px;
      z-index: 9998;
      white-space: nowrap;
      pointer-events: none;
    }

    .floating-tooltip::after {
      content: '';
      position: absolute;
      top: 100%;
      right: 28px;
      border: 6px solid transparent;
      border-top-color: #333;
    }

    /* Animations */
    @keyframes slideUp {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @keyframes pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
      }
      70% {
        box-shadow: 0 0 0 15px rgba(25, 118, 210, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
      }
    }

    /* Responsive */
    @media (max-width: 600px) {
      .pwa-prompt-card {
        max-height: 100vh;
        height: 100vh;
        max-width: 100%;
        border-radius: 0;
      }

      .prompt-header {
        border-radius: 0;
      }

      .benefits {
        grid-template-columns: 1fr;
      }

      .prompt-actions {
        flex-direction: column;
      }

      .floating-install-btn {
        bottom: 16px;
        right: 16px;
      }
    }
  `],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms', style({ opacity: 1 }))
      ])
    ]),
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateX(100px)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class PwaInstallPromptComponent implements OnInit {
  showFullPrompt = false;
  showMinimizedPrompt = false;
  showAnimation = false;
  showTooltip = false;
  platform: 'ios' | 'android' | 'desktop' | 'unknown' = 'unknown';
  currentStep = 0;
  canInstall = false;
  isInSafari = false;
  private deferredPrompt: any;
  private hasShownOnce = false;

  constructor(private iosNotificationService: IosNotificationService) {}

  ngOnInit() {
    this.detectPlatform();
    this.checkInstallationStatus();
    this.listenForInstallPrompt();
    
    // NE RIEN afficher automatiquement - l'utilisateur doit explicitement demander l'installation
    // Le bouton sera uniquement visible si l'utilisateur n'a jamais cliqué "Plus tard"
    // et que cela fait plus de 90 jours depuis la dernière apparition
  }

  private detectPlatform() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(userAgent)) {
      this.platform = 'ios';
      this.isInSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent);
    } else if (/android/.test(userAgent)) {
      this.platform = 'android';
    } else if (window.matchMedia('(min-width: 1024px)').matches) {
      this.platform = 'desktop';
    }
  }

  private checkInstallationStatus() {
    // Vérifier si l'app est déjà installée
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone ||
                        document.referrer.includes('android-app://');
    
    if (isStandalone) {
      this.showFullPrompt = false;
      this.showMinimizedPrompt = false;
    }
  }

  private listenForInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.canInstall = true;
    });

    // Écouter aussi l'événement custom
    window.addEventListener('pwainstallable', () => {
      this.deferredPrompt = (window as any).deferredPrompt;
      this.canInstall = true;
    });
  }

  private isAlreadyInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone;
  }

  private hasBeenDismissedRecently(): boolean {
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (!dismissed) return false;
    
    const dismissedTime = parseInt(dismissed, 10);
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    return daysSinceDismissed < 90; // Ne pas remontrer pendant 90 jours (3 mois)
  }

  private isPermanentlyDismissed(): boolean {
    return localStorage.getItem('pwa-prompt-never-show') === 'true';
  }

  neverShowAgain() {
    localStorage.setItem('pwa-prompt-never-show', 'true');
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    this.showFullPrompt = false;
    this.showMinimizedPrompt = false;
  }

  startInstallation() {
    if (this.platform === 'ios') {
      // Sur iOS, on ne peut pas déclencher l'installation par programme
      this.currentStep = 2; // Passer à l'étape 2 du guide
      this.showAnimation = true;
    } else if (this.deferredPrompt) {
      // Sur Android/Desktop, déclencher le prompt natif
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((result: any) => {
        if (result.outcome === 'accepted') {
          this.confirmInstallation();
        } else {
          this.installLater();
        }
        this.deferredPrompt = null;
      });
    }
  }

  confirmInstallation() {
    // L'utilisateur confirme avoir installé
    localStorage.setItem('pwa-installed', 'true');
    this.showFullPrompt = false;
    this.showMinimizedPrompt = false;
    
    // Recharger la page pour ouvrir en mode PWA si possible
    if (this.platform === 'ios') {
      alert('Parfait ! Maintenant ouvrez l\'application depuis l\'icône sur votre écran d\'accueil pour profiter de toutes les fonctionnalités.');
    } else {
      window.location.reload();
    }
  }

  installLater() {
    // Réduire le prompt
    this.showFullPrompt = false;
    this.showMinimizedPrompt = true;
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  }

  minimizePrompt() {
    this.showFullPrompt = false;
    this.showMinimizedPrompt = true;
  }

  reopenPrompt() {
    this.showFullPrompt = true;
    this.currentStep = 0;
  }

  toggleAnimation() {
    this.showAnimation = !this.showAnimation;
  }


  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('floating-install-btn')) {
      this.showTooltip = true;
    }
  }

  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('floating-install-btn')) {
      this.showTooltip = false;
    }
  }
}